import { useEffect } from 'react';
import { InvoicesStatuses } from 'store/invoices/types';
import { StyledTabs } from './styles';
import { TabItem } from './TabItem';
import { DashboardInvoicesData } from 'views/invoices/types';
import { invoiceStatusColorMap } from 'views/invoices/utils';

type Props = {
    archived: boolean | undefined;
    invoicesCounts: DashboardInvoicesData;
    activeFilter: InvoicesStatuses | null;
    onFilterChange: (status: InvoicesStatuses | null) => void;
    isCountsLoading?: boolean;
};

const VALID_ARCHIVED_FILTERS = [InvoicesStatuses.PAID, InvoicesStatuses.CANCELLED];

const DashboardHeader = ({ activeFilter, invoicesCounts, onFilterChange, archived, isCountsLoading }: Props) => {
    const items = [
        {
            key: 'ALL',
            label: <TabItem count={invoicesCounts.all} color="var(--brand-6)" text="All" loading={isCountsLoading} />,
        },
        {
            key: InvoicesStatuses.DRAFT,
            label: (
                <TabItem
                    count={invoicesCounts.draft}
                    color={invoiceStatusColorMap.DRAFT}
                    text="Draft"
                    loading={isCountsLoading}
                />
            ),
        },
        {
            key: InvoicesStatuses.AWAITING_PAYMENT,
            label: (
                <TabItem
                    count={invoicesCounts.toBePaid}
                    color={invoiceStatusColorMap.AWAITING_PAYMENT}
                    text="To Be Paid"
                    loading={isCountsLoading}
                />
            ),
        },
        {
            key: InvoicesStatuses.PAID,
            label: (
                <TabItem
                    count={invoicesCounts.paymentReceived}
                    color={invoiceStatusColorMap.PAID}
                    text="Paid"
                    loading={isCountsLoading}
                />
            ),
        },
        {
            key: InvoicesStatuses.PARTIALLY_PAID,
            label: (
                <TabItem
                    count={invoicesCounts.partiallyPaid}
                    color={invoiceStatusColorMap.PARTIALLY_PAID}
                    text="Partially Paid"
                    loading={isCountsLoading}
                />
            ),
        },
        {
            key: InvoicesStatuses.TO_BE_DUE,
            label: <TabItem count={invoicesCounts.aboutToBeDue} color={invoiceStatusColorMap.TO_BE_DUE} text="Due" />,
        },
        {
            key: InvoicesStatuses.OVERDUE,
            label: (
                <TabItem
                    count={invoicesCounts.overdue}
                    color={invoiceStatusColorMap.OVERDUE}
                    text="Overdue"
                    loading={isCountsLoading}
                />
            ),
        },
        {
            key: InvoicesStatuses.DISPUTED,
            label: (
                <TabItem
                    count={invoicesCounts.disputed}
                    color={invoiceStatusColorMap.DISPUTED}
                    text="Disputed"
                    loading={isCountsLoading}
                />
            ),
        },
        {
            key: InvoicesStatuses.CANCELLED,
            label: (
                <TabItem
                    count={invoicesCounts.cancelled}
                    color={invoiceStatusColorMap.CANCELLED}
                    text="Cancelled"
                    loading={isCountsLoading}
                />
            ),
        },
    ];

    const filteredItems = archived
        ? items.filter(
              (item) =>
                  item.key === InvoicesStatuses.PAID || item.key === InvoicesStatuses.CANCELLED || item.key === 'ALL'
          )
        : items;

    // Clear the filter when switching to archived and invalid archived filter is applied
    useEffect(() => {
        if (archived && activeFilter && !VALID_ARCHIVED_FILTERS.includes(activeFilter)) {
            onFilterChange(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archived]);

    return (
        <StyledTabs
            centered={!archived}
            $compact={!!archived}
            items={filteredItems}
            activeKey={activeFilter || 'ALL'}
            onChange={(val) => {
                const filter = val !== 'ALL' ? val : null;
                onFilterChange(filter as InvoicesStatuses);
            }}
        />
    );
};

export default DashboardHeader;
